/**
 * 商品オプション（自前実装）の PDP 側。
 *
 * 設計は Obsidian「2026-08-24 商品オプションの自前化 アーキテクチャ設計」第7・8節。
 *
 * やること:
 *   1. 選択を集めて `properties[_opt]`（機械用）と `properties[<軸名>]`（表示用）を埋める
 *   2. depends_on の軸を出し入れする
 *   3. 合計の目安を出す
 *
 * 価格の正は Cart Transform Function。ここの合計はあくまで表示で、
 * カートへ金額は送らない（送っても Function はクライアントの値を見ない）。
 */

class ProductOptionsComponent extends HTMLElement {
  /**
   * 選択が変わるたびに投げるイベント名。
   * 追従バー（sticky-add-to-cart.js）など、価格を出す側はこれを購読する。
   * Easify 時代はメイン価格の DOM 変化を MutationObserver で見ていたが、
   * 自前実装はメイン価格を書き換えないので、実数をイベントで配る。
   */
  static priceEvent = 'product-options:update';

  /** @type {object | null} 最後に計算した価格。イベントより後に現れた購読者が読む */
  priceState = null;

  connectedCallback() {
    this.root = this.querySelector('[data-form-id]');
    if (!this.root) return;

    this.readConfig();
    this.optField = this.root.querySelector('[data-opt-field]');

    this.root.addEventListener('change', () => this.sync());
    this.root.addEventListener('input', () => this.sync());

    // バリアント切替でセクションが morph され `data-base-price` が入れ替わる。
    // 設定を読み直して価格を出し直す（morph は要素を残すので connectedCallback は再実行されない）。
    this.onVariantUpdate = () => this.sync();
    document.addEventListener('variant:update', this.onVariantUpdate);

    this.sync();
    this.guardAddToCart();
  }

  disconnectedCallback() {
    if (this.onVariantUpdate) document.removeEventListener('variant:update', this.onVariantUpdate);
  }

  /**
   * データ属性から設定を読む。バリアント切替のたびに読み直す必要があるので
   * connectedCallback ではなく sync から呼ぶ。
   */
  readConfig() {
    this.basePrice = Number(this.root.dataset.basePrice || 0);
    this.floor = Number(this.root.dataset.floor || 0);
    this.moneyFormat = this.root.dataset.moneyFormat || '¥{{amount_no_decimals}}';
    this.productId = this.root.dataset.productId || '';
  }

  /**
   * 必須が埋まっていないままカートに入れられないようにする。
   *
   * テーマは submit を JS で横取りして AJAX でカートに入れるので、
   * `required` 属性だけではブラウザ標準の検証が走らない。
   * capture フェーズでクリックを捕まえて、埋まっていなければ止める。
   *
   * **`window` に登録する。`document` ではテーマ側より後になる。**
   * Horizon の `on:click` は component.js がモジュール読み込み時に
   * `document` の capture へ1本だけ登録する。このブロックが接続されるのはその後なので、
   * 同じ `document` の capture に足すと登録順で負けて、カートへ飛ぶアニメーション
   * （product-form.js の #animateFlyToCart）が先に再生されてしまう。
   * `window` の capture なら伝播が `document` に届く前に止められる。
   */
  guardAddToCart() {
    const form = document.getElementById(this.root.dataset.formId);
    if (!form) return;

    const block = (e, missing) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.showErrors(missing);
    };

    const onClick = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const btn = target?.closest('button[type="submit"], button[name="add"]');
      if (!btn || !form.contains(btn)) return;
      const missing = this.findMissing();
      if (!missing.length) return;
      block(e, missing);
    };
    window.addEventListener('click', onClick, true);

    // Enter キーでの submit も塞ぐ。これも window でないとテーマ側が先に走る。
    const onSubmit = (e) => {
      if (e.target !== form) return;
      const missing = this.findMissing();
      if (!missing.length) return;
      block(e, missing);
    };
    window.addEventListener('submit', onSubmit, true);
  }

  /** 未入力の必須軸を返す。隠れている軸（depends_on で非表示）は対象外。 */
  findMissing() {
    const missing = [];
    this.root.querySelectorAll('.__axis[data-axis]').forEach((axisEl) => {
      if (axisEl.hidden) return;
      const inputs = axisEl.querySelectorAll('input, select, textarea');
      const required = Array.from(inputs).some(
        (el) => el.hasAttribute('required') || el.dataset.wasRequired,
      );
      if (!required) return;

      const radios = axisEl.querySelectorAll('input[type="radio"]');
      if (radios.length) {
        if (!axisEl.querySelector('input[type="radio"]:checked')) missing.push(axisEl);
        return;
      }
      const field = axisEl.querySelector('select, input[type="text"], input[type="number"], textarea');
      if (field && !String(field.value || '').trim()) missing.push(axisEl);
    });
    return missing;
  }

  showErrors(missing) {
    // 前回のエラー表示を消す
    this.root.querySelectorAll('.__error').forEach((el) => el.remove());
    this.root.querySelectorAll('.__axis--invalid').forEach((el) =>
      el.classList.remove('__axis--invalid'),
    );

    missing.forEach((axisEl) => {
      axisEl.classList.add('__axis--invalid');
      const label = axisEl.querySelector('.__label');
      const name = label ? label.textContent.replace('*', '').trim() : '';
      const msg = document.createElement('p');
      msg.className = '__error';
      msg.setAttribute('role', 'alert');
      // 自由入力の軸に「選択してください」と出ると操作が分からない
      const isFreeInput = axisEl.querySelector(
        'textarea, input[type="text"], input[type="number"]',
      );
      msg.textContent = `「${name}」を${isFreeInput ? '入力' : '選択'}してください`;
      axisEl.appendChild(msg);
    });

    const first = missing[0];
    first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = first.querySelector('select, textarea, input:not([type="hidden"])');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  /** 軸ごとの現在値。{axisKey: {value, label, delta}} */
  collect() {
    const out = {};
    this.root.querySelectorAll('.__axis[data-axis]').forEach((axisEl) => {
      const key = axisEl.dataset.axis;
      if (axisEl.hidden) return;

      const checked = axisEl.querySelector('input[type="radio"]:checked');
      if (checked) {
        out[key] = {
          value: checked.value,
          label: checked.dataset.label || checked.value,
          delta: Number(checked.dataset.delta || 0),
        };
        return;
      }

      const select = axisEl.querySelector('select');
      if (select && select.value) {
        const opt = select.selectedOptions[0];
        out[key] = {
          value: select.value,
          label: opt?.dataset.label || select.value,
          delta: Number(opt?.dataset.delta || 0),
        };
        return;
      }

      const field = axisEl.querySelector('input[type="text"], input[type="number"], textarea');
      if (field && field.value.trim()) {
        // 自由入力の軸は価格に影響しない（寸法・刻印など）。表示だけ。
        out[key] = { value: field.value.trim(), label: field.value.trim(), delta: 0 };
      }
    });
    return out;
  }

  /** depends_on: 依存先の値が選ばれている軸だけ出す */
  applyDependencies(selected) {
    const chosenValues = new Set(Object.values(selected).map((s) => s.value));
    let changed = false;

    this.root.querySelectorAll('.__axis[data-depends-on]').forEach((axisEl) => {
      const deps = axisEl.dataset.dependsOn.split(',').filter(Boolean);
      const shouldShow = deps.some((d) => chosenValues.has(d));
      if (axisEl.hidden === !shouldShow) return;
      axisEl.hidden = !shouldShow;
      changed = true;

      // 隠した軸の入力は残さない。required も外さないと submit がブロックされる。
      axisEl.querySelectorAll('input, select, textarea').forEach((el) => {
        if (!shouldShow) {
          if (el.type === 'radio' || el.type === 'checkbox') el.checked = false;
          else el.value = '';
        } else if (el.tagName === 'SELECT' && !el.value) {
          // 空欄を持たない select（旧サイトと同じく先頭が既定の軸）は、
          // 隠したときに value='' にした時点で選択が外れている。出し直すときに既定へ戻す。
          const first = el.options[0];
          if (first && first.value) el.selectedIndex = 0;
        }
        if (el.required !== undefined) {
          if (!shouldShow) {
            if (el.hasAttribute('required')) {
              el.dataset.wasRequired = '1';
              el.removeAttribute('required');
            }
          } else if (el.dataset.wasRequired) {
            el.setAttribute('required', '');
          }
        }
      });
    });
    return changed;
  }

  /**
   * 最小単位（cents）を money_format に流し込む。
   *
   * 単位に注意: `variant.price` は Liquid の時点で既に最小単位（¥48,900 なら 4890000）。
   * 一方 `price_delta` は number_integer で **円** なので、cents に揃えるには ×100 が要る。
   * ここを混ぜると ¥8,437 が ¥84 になる。
   */
  formatMoney(cents) {
    const major = Math.round(cents) / 100;
    const noDecimals = Math.round(major).toLocaleString('ja-JP');
    const withDecimals = major.toLocaleString('ja-JP', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
  }

  sync() {
    this.readConfig();
    let selected = this.collect();

    // 依存関係で表示が変わったら、隠れた軸の値を落としてから集め直す
    if (this.applyDependencies(selected)) {
      selected = this.collect();
    }

    // 機械用: axisKey=valueKey を | で連結。表示名を変えても壊れない安定キー
    const optString = Object.entries(selected)
      .map(([axisKey, s]) => `${axisKey}=${s.value}`)
      .join('|');
    if (this.optField) this.optField.value = optString;

    // 表示用: 軸ごとの hidden を埋める。
    // 空文字の property は Shopify 側では落ちず、そのまま注文に残る（2026-08-25 実測）。
    // 値の無いものは name を外して送信対象から除く。
    this.root.querySelectorAll('[data-display-for]').forEach((el) => {
      const s = selected[el.dataset.displayFor];
      if (s && s.label) {
        // 注文画面・通知メールは property の文字列をそのまま出すので、差額を選択肢名の後ろに付ける。
        // 価格の正は Function。これは表示だけ（0円と自由入力は付けない）。
        const sign = s.delta > 0 ? '+' : '-';
        el.value = s.delta
          ? `${s.label} (${sign}¥${Math.abs(s.delta).toLocaleString('ja-JP')})`
          : s.label;
        if (el.dataset.name) {
          el.name = el.dataset.name;
          delete el.dataset.name;
        }
      } else {
        el.value = '';
        if (el.name) {
          el.dataset.name = el.name;
          el.removeAttribute('name');
        }
      }
    });

    this.priceState = this.computePrice(selected);
    this.updateTotal();
    this.publishPrice();

    // 入力が入った軸のエラー表示は消す
    this.root.querySelectorAll('.__axis--invalid').forEach((axisEl) => {
      if (selected[axisEl.dataset.axis]) {
        axisEl.classList.remove('__axis--invalid');
        axisEl.querySelectorAll('.__error').forEach((el) => el.remove());
      }
    });
  }

  /**
   * 現在の選択から価格を出す。表示に使う文字列もここで作って配る
   * （購読側に money_format の解釈を持たせないため）。
   *
   * 単位: `delta` と `floor` は「円」、`basePrice` は最小単位（cents）。cents に揃えてから足す。
   * @param {Record<string, {value: string, label: string, delta: number}>} selected
   */
  computePrice(selected) {
    const options = Object.values(selected).reduce((sum, s) => sum + s.delta, 0) * 100;
    const total = Math.max(this.floor * 100, this.basePrice + options);
    const sign = options > 0 ? '+' : '−';

    return {
      productId: this.productId,
      base: this.basePrice,
      options,
      total,
      // 下限（floor）に張り付くと 本体+オプション ≠ 合計 になる。
      // その状態で内訳を出すと数字が合わないので、表示側は畳んで合計だけ出す。
      clamped: total !== this.basePrice + options,
      formatted: {
        base: this.formatMoney(this.basePrice),
        options: options === 0 ? '' : `${sign}${this.formatMoney(Math.abs(options))}`,
        total: this.formatMoney(total),
      },
    };
  }

  /** 価格が変わったことを外へ知らせる（追従バーの内訳がこれで動く） */
  publishPrice() {
    if (!this.priceState) return;
    this.dispatchEvent(
      new CustomEvent(ProductOptionsComponent.priceEvent, {
        bubbles: true,
        composed: true,
        detail: this.priceState,
      }),
    );
  }

  updateTotal() {
    const totalEl = this.root.querySelector('[data-total]');
    if (!totalEl || !this.priceState) return;

    const { options, clamped, formatted } = this.priceState;
    const showBreakdown = options !== 0 && !clamped;

    totalEl.hidden = false;
    totalEl.querySelector('[data-total-base]').textContent = formatted.base;
    totalEl.querySelector('[data-total-base]').closest('.__total-row').hidden = !showBreakdown;

    const optionsRow = totalEl.querySelector('[data-total-options-row]');
    optionsRow.hidden = !showBreakdown;
    if (showBreakdown) {
      optionsRow.querySelector('[data-total-options]').textContent = formatted.options;
    }

    totalEl.querySelector('[data-total-sum]').textContent = formatted.total;
  }
}

if (!customElements.get('product-options-component')) {
  customElements.define('product-options-component', ProductOptionsComponent);
}
